<template>
    <div class="w-full bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-5">
        <div class="flex justify-between items-center mb-4">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white">{{ $t('playlistDescriptionCard.description', 'Descrição') }}</h3>
            <span class="text-[11px] font-medium text-gray-400">{{ playlist.description ? playlist.description.length : 0 }} / 255</span>
        </div>
        
        <div class="w-full border border-gray-100 dark:border-slate-700 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all relative">
            <div v-if="isGenerating" class="absolute inset-0 bg-white/50 dark:bg-slate-800/50 flex items-center justify-center z-10">
                <svg class="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            </div>
            <!-- eslint-disable-next-line vue/no-mutating-props -->
            <textarea v-model="playlist.description" maxlength="255" rows="5"
                @blur="$emit('save-description')"
                :disabled="(!playlist.permissions || !playlist.permissions.includes('editor')) || isGenerating"
                :placeholder="$t('playlistDescriptionCard.placeholder', 'Adicione uma descrição...')"
                class="w-full resize-none border-none bg-transparent text-sm text-gray-700 dark:text-gray-300 focus:ring-0 p-4 leading-relaxed"></textarea>
        </div>
        
        <div class="flex justify-between items-center mt-4" v-if="playlist.permissions && playlist.permissions.includes('editor')">
            <span class="text-[10px] text-gray-400">{{ $t('playlistDescriptionCard.autoSave', 'Salvo automaticamente ao sair do campo') }}</span>
            <div class="flex items-center gap-2">
                <button v-if="hasAiFeature" @click="generateDescription" :title="$t('playlistDescriptionCard.generateAi', 'Gerar com IA')" :disabled="isGenerating" class="px-3 py-2 text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center">
                    <SparklesIcon class="w-5 h-5" />
                </button>
                <button @click="$emit('save-description')" :disabled="isGenerating || isSaving" class="flex items-center justify-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors disabled:opacity-50 min-w-[80px]">
                    <svg v-if="isSaving" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    <span v-else>{{ $t('playlistDescriptionCard.save', 'Salvar') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue';
import { SparklesIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import userStore from '@/store/user.store';
import playlistService from '@/service/playlist.service';
import toastStore from '@/store/toast.store';

const props = defineProps({
    playlist: {
        type: Object,
        required: true
    },
    isSaving: {
        type: Boolean,
        default: false
    }
});

defineEmits(['save-description']);

const { locale, t } = useI18n();

const hasAiFeature = computed(() => userStore.getters.hasAiFeature);
const isGenerating = ref(false);

const generateDescription = async () => {
    isGenerating.value = true;
    try {
        const lang = locale.value || 'pt-BR';
        
        const playlistData = {
            name: props.playlist.name,
            tags: (props.playlist.tags || []).map(t => t.name),
            songs: (props.playlist.songs || []).map(s => {
                const songInfo = { title: s.song?.title || 'Unknown' };
                if (s.song?.tags) {
                    songInfo.tags = s.song.tags.map(t => t.name);
                }
                return songInfo;
            })
        };

        const response = await playlistService.generateDescription(lang, playlistData);
        
        if (response.ok) {
            const data = await response.json();
            // eslint-disable-next-line vue/no-mutating-props
            props.playlist.description = data.description;
            toastStore.dispatch("addMessage", {message: t('playlistDescriptionCard.generatedAi', 'Descrição gerada com IA'), icon: 'success'});
        } else {
            const err = await response.json();
            toastStore.dispatch("addMessage", {message: err.error || t('playlistDescriptionCard.generateError', 'Erro ao gerar descrição'), icon: 'error'});
        }
    } catch (e) {
        toastStore.dispatch("addMessage", {message: t('playlistDescriptionCard.connectionError', 'Erro de conexão'), icon: 'error'});
    } finally {
        isGenerating.value = false;
    }
};
</script>
